import BrandAttributeField from '../BrandAttributeField/BrandAttributeField';
import styles from './AttributeFields.module.scss';

const AttributeFields = ({ attributes, attributeValues, errors, onAttributeChange, clearAttributeError }) => {

  // Filter out SKU-related attributes that should only appear in SKU section
  const filteredAttributes = attributes.filter(attribute => {
    const skuRelatedFields = [
      'SellerSku', 'sku', 'price', 'package_weight', 'package_length',
      'package_width', 'package_height', 'quantity'
    ];
    return !skuRelatedFields.includes(attribute.name);
  });

  // Handle number input with validation and scroll prevention
  const handleNumberInput = (name, value) => {
    // Remove any non-numeric characters except decimal point
    const numericValue = value.replace(/[^\d.]/g, '');
    
    // Ensure only one decimal point
    const parts = numericValue.split('.');
    const formattedValue = parts.length > 2 
      ? parts[0] + '.' + parts.slice(1).join('')
      : numericValue;
    
    onAttributeChange(name, formattedValue);
    clearAttributeError(name);
  };

  const renderAttributeInput = (attribute) => {
    const { name, input_type, label, options = [] } = attribute;
    const value = attributeValues[name] || '';
    const errorKey = `attribute_${name}`;
    const hasError = errors[errorKey];

    // Special handling for brand attribute
    if (
      (typeof name === 'string' && name.toLowerCase() === 'brand') ||
      (typeof label === 'string' && label.toLowerCase() === 'brand')
    ) {
      return (
        <BrandAttributeField
          key={name}
          attribute={attribute}
          value={value}
          error={hasError ? errors[errorKey] : ''}
          onAttributeChange={onAttributeChange}
          clearAttributeError={clearAttributeError}
        />
      );
    }

    const commonProps = {
      name: name,
      value: value,
      onChange: (e) => {
        onAttributeChange(name, e.target.value);
        clearAttributeError(name);
      },
      onFocus: () => clearAttributeError(name),
      className: `${styles.input_field}`,
      required: attribute.is_mandatory === 1,
      style: { color: value ? '#000000' : '#797775' }
    };

    // Handle multi-select change separately
    const handleMultiSelectChange = (e) => {
      const selectedOptions = Array.from(e.target.selectedOptions).map(option => option.value);
      onAttributeChange(name, selectedOptions);
      clearAttributeError(name);
    };

    const renderInputField = () => {
      switch (input_type) {
        case 'text':
          return (
            <input {...commonProps} type="text" placeholder={`Enter ${label}`} />
          );
        case 'numeric':
          return (
            <input 
              type="text"
              inputMode="decimal"
              name={name}
              value={value}
              onChange={(e) => handleNumberInput(name, e.target.value)}
              onWheel={(e) => e.target.blur()}
              onFocus={() => clearAttributeError(name)}
              className={`${styles.input_field}`}
              required={attribute.is_mandatory === 1}
              placeholder={`Enter ${label}`}
              min="0"
              // style={{ color: value ? '#000000' : '#797775' }}
            />
          );
        case 'richText':
          return (
            <textarea {...commonProps} placeholder={`Enter ${label}`} rows="4" className={`${styles.textarea_field}`} />
          );
        case 'singleSelect':
        case 'enumInput':
          return (
            <select {...commonProps} className={`${styles.select_field}`}>
              <option value="" disabled style={{ color: '#797775' }}>
                Select {label}
              </option>
              {options.map(option => (
                <option key={option.id} value={option.id} style={{ color: '#000000' }}>
                  {option.name}
                </option>
              ))}
            </select>
          );
        case 'multiSelect':
        case 'multiEnumInput':
          // Ensure value is an array
          let selectedValues = Array.isArray(value) ? value : (value ? [value] : []);

          // If the array has a single comma-separated string, split it into multiple values
          if (selectedValues.length === 1 && selectedValues[0].includes(',')) {
            selectedValues = selectedValues[0].split(',').map(v => v.trim());
          }

          // Map selected names (like "Gujarati") to option IDs
          selectedValues = selectedValues.map(val => {
            const matchedOption = options.find(opt =>
              opt.name.toLowerCase() === val.toLowerCase() || opt.en_name?.toLowerCase() === val.toLowerCase()
            );
            return matchedOption ? matchedOption.id : val;
          });

          return (
            <div className={`${styles.multi_select_wrapper}`}>
              <select
                multiple
                name={name}
                value={selectedValues}
                onChange={handleMultiSelectChange}
                onFocus={() => clearAttributeError(name)}
                className={`${styles.select_field}`}
                required={attribute.is_mandatory === 1}
                style={{ color: selectedValues.length > 0 ? '#000000' : '#797775' }}
              >
                <option value="" disabled style={{ color: '#797775' }}>
                  Select {label}
                </option>
                {options.map(option => (
                  <option key={option.id} value={option.id} style={{ color: '#000000' }}>
                    {option.name}
                  </option>
                ))}
              </select>
              {selectedValues.length > 0 && (
                <div className={`${styles.multi_select_selected}`}>
                  {selectedValues.length} item(s) selected
                </div>
              )}
              <div className={`${styles.multi_select_info}`}>
                Hold Ctrl/Cmd to select multiple options
              </div>
            </div>
          );
        case 'date':
          return (
            <input {...commonProps} type="date" />
          );
        case 'img':
          return (
            <input {...commonProps} type="file" />
          );
        // default:
        //   return (
        //     <input {...commonProps} type="text" placeholder={`Enter ${label}`} />
        //   );
      }
    };

    return (
      <div key={name} className={`${styles.label_input} ${hasError ? styles.error : ''}`}>
        <label>
          {label}
          {attribute.is_mandatory === 1 && <span>*</span>}
        </label>
        <div className={`${styles.input_container}`}>
          {renderInputField()}
          {hasError && <span className={`${styles.errorMessage}`}>{errors[errorKey]}</span>}
        </div>
      </div>
    );
  };

  if (filteredAttributes.length === 0) {
    return null;
  }

  return (
    <div className={`${styles.attributes_section}`}>
      <h3>Product Attributes</h3>
      {filteredAttributes.map((attribute) => renderAttributeInput(attribute))}
    </div>
  );
};

export default AttributeFields;